/** Brief hold avoids black frames when useTracks drops a publication during room churn. */
export const LIVEKIT_TRACK_HOLD_MS = 500;
/** Cold-start / churn hold while waiting for the agent publisher to reappear. */
export const LIVEKIT_PREVIEW_PUBLISHER_WAIT_HOLD_MS = 8000;

export type LiveKitTrackHoldDecision =
  | { action: 'keep' }
  | { action: 'clear' }
  | { action: 'schedule'; holdMs: number };

/**
 * Decide whether to keep, clear, or briefly schedule-clear the last LiveKit preview frame.
 *
 * The long publisher-wait window must not keep a former media-agent take on screen after the
 * layer has moved on (hidden, or expecting a different track SID).
 */
export function resolveLiveKitTrackHold(input: {
  hasInstantTrack: boolean;
  layerVisible: boolean;
  heldSid: string | null;
  candidateSids: readonly string[];
  authorizedTrackSids: readonly string[];
  componentId: string;
  publisherPlayerId?: string;
}): LiveKitTrackHoldDecision {
  if (input.hasInstantTrack) {
    return { action: 'keep' };
  }

  if (input.layerVisible === false) {
    return { action: 'clear' };
  }

  const { heldSid, candidateSids, authorizedTrackSids } = input;
  if (heldSid) {
    if (candidateSids.length > 0 && !candidateSids.includes(heldSid)) {
      return { action: 'clear' };
    }
    if (authorizedTrackSids.length > 0 && !authorizedTrackSids.includes(heldSid)) {
      return { action: 'clear' };
    }
  }

  const awaitingPublisher =
    Boolean(input.publisherPlayerId) && (candidateSids.length === 0 || input.componentId === 'livekitTrackMediaAgent');

  return {
    action: 'schedule',
    holdMs: awaitingPublisher ? LIVEKIT_PREVIEW_PUBLISHER_WAIT_HOLD_MS : LIVEKIT_TRACK_HOLD_MS,
  };
}

/** True when look-ahead / on-air layers still paint media-agent visuals (LiveKit or deck shield). */
export function previewLayersLookLikeMediaAgent(
  layers: ReadonlyArray<{ componentId?: string; itemId?: string; data?: Record<string, string> }>,
): boolean {
  return layers.some(
    (layer) =>
      layer.componentId === 'livekitTrackMediaAgent' ||
      (typeof layer.itemId === 'string' && layer.itemId.startsWith('mediaAgent:')) ||
      layer.data?.trackSource === 'mediaAgent',
  );
}
